import React, { useState, useEffect } from 'react'
import { FaEnvelope, FaSave, FaUndo } from 'react-icons/fa'
import settingsService from '../services/settingsService'
import { toast } from 'react-toastify'
import Loader from './Loader'

const DEFAULT_SETTINGS = {
  email_sender_name: 'Himachal Yatra',
  email_subject_prefix: 'Himachal Yatra',
  email_footer_signoff: 'Himachal Yatra Team',
  email_copyright: '© 2026 Himachal Yatra. All rights reserved.',
}

const EmailTemplateSettings = () => {
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const response = await settingsService.getAllSettings('email')
      if (response.settings && response.settings.length > 0) {
        const loaded = { ...DEFAULT_SETTINGS }
        for (const s of response.settings) {
          if (s.setting_key in loaded) {
            loaded[s.setting_key] = s.setting_value || DEFAULT_SETTINGS[s.setting_key]
          }
        }
        setSettings(loaded)
      }
    } catch {
      toast.error('Failed to load email template settings')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      await settingsService.updateMultipleSettings(settings)
      toast.success('Email template settings saved successfully')
    } catch {
      toast.error('Failed to save email template settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    loadSettings()
  }

  if (loading) return <Loader message="Loading email settings..." />

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FaEnvelope className="text-purple-600" />
            Email Template Settings
          </h2>
          <p className="text-gray-500 mt-1">Configure branding used in all outgoing emails</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <FaUndo /> Reset
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
          >
            <FaSave /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 space-y-6">
        <div>
          <label
            htmlFor="email_sender_name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Sender Name
          </label>
          <input
            id="email_sender_name"
            type="text"
            value={settings.email_sender_name}
            onChange={(e) => handleChange('email_sender_name', e.target.value)}
            placeholder="Himachal Yatra"
            className="input-field"
          />
          <p className="text-xs text-gray-400 mt-1">
            Appears in the "From" field of all outgoing emails
          </p>
        </div>

        <div>
          <label
            htmlFor="email_subject_prefix"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Subject Line Prefix
          </label>
          <input
            id="email_subject_prefix"
            type="text"
            value={settings.email_subject_prefix}
            onChange={(e) => handleChange('email_subject_prefix', e.target.value)}
            placeholder="Himachal Yatra"
            className="input-field"
          />
          <p className="text-xs text-gray-400 mt-1">
            Brand name used in email subject lines (e.g., "Booking Confirmed #123 - Himachal Yatra")
          </p>
        </div>

        <div>
          <label
            htmlFor="email_footer_signoff"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Footer Sign-off
          </label>
          <input
            id="email_footer_signoff"
            type="text"
            value={settings.email_footer_signoff}
            onChange={(e) => handleChange('email_footer_signoff', e.target.value)}
            placeholder="Himachal Yatra Team"
            className="input-field"
          />
          <p className="text-xs text-gray-400 mt-1">
            Team sign-off text at the end of emails (e.g., "Best regards, Himachal Yatra Team")
          </p>
        </div>

        <div>
          <label htmlFor="email_copyright" className="block text-sm font-medium text-gray-700 mb-1">
            Copyright Text
          </label>
          <input
            id="email_copyright"
            type="text"
            value={settings.email_copyright}
            onChange={(e) => handleChange('email_copyright', e.target.value)}
            placeholder="© 2026 Himachal Yatra. All rights reserved."
            className="input-field"
          />
          <p className="text-xs text-gray-400 mt-1">
            Copyright line shown in the footer of every email
          </p>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preview</h3>
        <div className="border rounded-lg overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-purple-700 text-white p-6 text-center">
            <h2 className="text-xl font-bold">Sample Email</h2>
            <p className="text-purple-100">{settings.email_sender_name}</p>
          </div>
          <div className="bg-gray-50 p-6">
            <p className="text-gray-700">Dear Customer,</p>
            <p className="text-gray-600 mt-2">This is a preview of how your emails will look.</p>
            <p className="text-gray-700 mt-4">
              Best regards,
              <br />
              <strong>{settings.email_footer_signoff}</strong>
            </p>
          </div>
          <div className="text-center py-3 border-t text-gray-400 text-sm">
            {settings.email_copyright}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailTemplateSettings
